import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { guardarInscricaoCodigo } from '@/lib/inscricaoCodigo';
import Sheet from '@/components/ui/Sheet';
import CampoTexto from '@/components/base/CampoTexto';

// ---------------------------------------------------------------------------
// "Tenho um código" (app instalado): campo para digitar o código do convite.
// Acha o evento pelo código (rpc evento_do_codigo), guarda o código como a
// página do evento faz e abre /e/{slug}?c={codigo}. Um evento abre direto;
// vários mostram a lista para escolher.
// ---------------------------------------------------------------------------

interface LinhaCodigo {
  slug: string;
  evento: string | null;
}

export function FolhaTenhoCodigo({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [opcoes, setOpcoes] = useState<LinhaCodigo[]>([]);

  function fechar() {
    setCodigo('');
    setErro(null);
    setOpcoes([]);
    setEnviando(false);
    aoFechar();
  }

  // Guarda o código do mesmo jeito que a página do evento (com as modalidades
  // liberadas) e abre o evento.
  async function abrir(slug: string, cod: string) {
    try {
      const { data } = await supabase.rpc('modalidades_liberadas', {
        p_evento_slug: slug,
        p_codigo: cod,
      });
      const ids = (data as number[]) || [];
      if (ids.length > 0) guardarInscricaoCodigo(slug, cod, ids);
    } catch {
      /* segue mesmo sem guardar */
    }
    fechar();
    navigate(`/e/${slug}?c=${encodeURIComponent(cod)}`);
  }

  async function continuar() {
    const cod = codigo.trim();
    if (!cod) {
      setErro('Digite o código do convite.');
      return;
    }
    setEnviando(true);
    setErro(null);
    setOpcoes([]);
    try {
      const { data, error } = await supabase.rpc('evento_do_codigo', { p_codigo: cod });
      if (error) throw error;
      const linhas = (data as LinhaCodigo[]) || [];
      if (linhas.length === 0) {
        setErro('Código não encontrado. Confira com a direção.');
        return;
      }
      if (linhas.length === 1) {
        await abrir(linhas[0].slug, cod);
        return;
      }
      setOpcoes(linhas);
    } catch {
      setErro('Não conseguimos verificar esse código. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Sheet aberto={aberto} titulo="Tenho um código" aoFechar={fechar}>
      <p className="text-[14px] leading-relaxed text-foreground-600">
        Digite o código do cartaz ou do convite que a direção te passou.
      </p>

      <div className="mt-4">
        <CampoTexto
          id="codigo-convite"
          name="codigo-convite"
          label="Código do convite"
          value={codigo}
          onChange={(v) => {
            setCodigo(v);
            setErro(null);
            setOpcoes([]);
          }}
          error={erro}
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          placeholder="Ex.: CAMPISTA27"
        />
      </div>

      {opcoes.length > 1 && (
        <div className="mt-4 space-y-2">
          <p className="text-[13px] font-semibold text-foreground-600">
            Este código abre mais de um evento. Escolha um:
          </p>
          {opcoes.map((o) => (
            <button
              key={o.slug}
              type="button"
              onClick={() => void abrir(o.slug, codigo.trim())}
              className="aperta flex w-full cursor-pointer items-center justify-between gap-3 rounded-[16px] border border-background-200 bg-background-50 px-4 py-3 text-left transition-colors hover:bg-background-100"
            >
              <span className="min-w-0 truncate text-[15px] font-semibold text-foreground-950">
                {o.evento || o.slug}
              </span>
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden />
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={continuar}
        disabled={enviando}
        className="aperta mt-5 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 text-base font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className={enviando ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} aria-hidden />
        </span>
        {enviando ? 'Verificando…' : 'Continuar'}
      </button>
    </Sheet>
  );
}

// Link discreto do Início que abre a folha.
export default function BotaoTenhoCodigo({ className = '' }: { className?: string }) {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        className={`aperta inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-primary-600 ${className}`}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-qr-scan-2-line" aria-hidden />
        </span>
        Tenho um código
      </button>
      <FolhaTenhoCodigo aberto={aberto} aoFechar={() => setAberto(false)} />
    </>
  );
}